import type { InferOutput } from "valibot";
import { boolean, object, picklist } from "valibot";
import { initIntegrationDefinition } from "../Integration";
import type { IncompleteIntegrationDefinition } from "../types";
import { useTranslation } from "../../state/i18n";
import { Checkbox } from "../../components/checkbox/Checkbox";
import { TextInput } from "../../components/inputs/TextInput";
import { MAX_CHARACTERS_SM, PORT_MAX_VALUE } from "../../constants";
import { NumberInput } from "../../components/inputs/NumberInput";
import { errorTranslation, integerValue, requireText } from "../../utils/validation/base";
import MixedInput from "../../components/inputs/MixedInput";
import { parseStringTemplate, stringTemplateToString } from "../../utils/stringTemplateUtil";
import { createMemo } from "solid-js";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { useFormServices } from "../../services/formServices";
import type { FieldType } from "../../types";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import type { VariableType } from "../../variables/types";
import { toVariableValueOption } from "../../variables/variableUtil";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { SingleSelectInput } from "../../components/menu/Select";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

const ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

const ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
];

type ConfigurationDto = {
  protocol: "ftps" | "sftp" | "ftp";
  disableSslVerification: boolean;
  implicitftps: boolean;
  server: string;
  port: number;
  path: string;
  autoappendpath: boolean;
  jsonFilename?: string;
  username?: string;
  password?: string;
  includepdf: boolean;
  storepdfwithoutrecipient: boolean;
  includeimages: boolean;
};

const Schema = object({
  protocol: picklist(["ftps", "sftp", "ftp"], errorTranslation(keyFromSelector(($) => $.VALIDATION_ENUM))),
  disableSslVerification: boolean(),
  implicitFtps: boolean(),
  server: requireText(MAX_CHARACTERS_SM),
  port: integerValue(0, PORT_MAX_VALUE),
  path: stringTemplate(
    ALLOWED_PLACEHOLDER_FIELDS,
    ALLOWED_PLACEHOLDER_VARIABLES,
    ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_SM,
  ),
  autoAppendPath: boolean(),
  jsonFilename: stringTemplate(
    ALLOWED_PLACEHOLDER_FIELDS,
    ALLOWED_PLACEHOLDER_VARIABLES,
    ALLOWED_PLACEHOLDER_META_VARIABLES,
    MAX_CHARACTERS_SM,
    false,
  ),
  username: requireText(MAX_CHARACTERS_SM),
  password: requireText(MAX_CHARACTERS_SM),
  includePdf: boolean(),
  storePdfWithoutRecipient: boolean(),
  includeFiles: boolean(),
});

export type FTPSIntegrationConfiguration = InferOutput<typeof Schema>;
const FTPSIntegration: IncompleteIntegrationDefinition<FTPSIntegrationConfiguration, ConfigurationDto> = {
  type: "sftp",
  legacy: {
    namespace: "com.moreapp.plugins",
    key: "sftp",
    version: 10,
  },
  schema: Schema,
  icon: integrationsIcons["sftp"],
  titleKey: keyFromSelector(($) => $.FTPS_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.FTPS_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: () => ({
    protocol: "ftps",
    disableSslVerification: false,
    implicitFtps: true,
    server: "",
    port: 0,
    path: [],
    autoAppendPath: true,
    jsonFilename: [{ type: "literal", value: "submission.json" }],
    username: "",
    password: "",
    includePdf: false,
    storePdfWithoutRecipient: false,
    includeFiles: false,
  }),
  propertyKeys: {
    protocol: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_PROTOCOL_LABEL),
    disableSslVerification: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_DISABLE_SSL_VERIFICATION_LABEL),
    implicitFtps: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_IMPLICIT_FTPS_LABEL),
    server: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_SERVER_LABEL),
    port: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_PORT_LABEL),
    path: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_PATH_LABEL),
    autoAppendPath: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_AUTO_APPEND_PATH_LABEL),
    jsonFilename: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_JSON_FILENAME_LABEL),
    username: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_USERNAME_LABEL),
    password: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_PASSWORD_LABEL),
    includePdf: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_INCLUDE_PDF_LABEL),
    storePdfWithoutRecipient: keyFromSelector(
      ($) => $.FTPS_INTEGRATION_CONFIGURATION_STORE_PDF_WITHOUT_RECIPIENT_LABEL,
    ),
    includeFiles: keyFromSelector(($) => $.FTPS_INTEGRATION_CONFIGURATION_INCLUDE_FILES_LABEL),
  },
  fromDto: (configuration, fields, variables) => ({
    protocol: configuration.protocol,
    disableSslVerification: configuration.disableSslVerification,
    implicitFtps: configuration.implicitftps,
    server: configuration.server,
    port: configuration.port,
    path: parseStringTemplate(configuration.path, fields, variables),
    autoAppendPath: configuration.autoappendpath,
    jsonFilename: parseStringTemplate(configuration.jsonFilename, fields, variables),
    username: configuration.username,
    password: configuration.password,
    includePdf: configuration.includepdf,
    storePdfWithoutRecipient: configuration.storepdfwithoutrecipient,
    includeFiles: configuration.includeimages,
  }),
  toDto: (configuration, fields, variables) => ({
    protocol: configuration.protocol,
    disableSslVerification: configuration.disableSslVerification,
    implicitftps: configuration.implicitFtps,
    server: configuration.server,
    port: configuration.port,
    path: stringTemplateToString(configuration.path, fields, variables),
    autoappendpath: configuration.autoAppendPath,
    jsonFilename: stringTemplateToString(configuration.jsonFilename, fields, variables),
    username: configuration.username,
    password: configuration.password,
    includepdf: configuration.includePdf,
    storepdfwithoutrecipient: configuration.storePdfWithoutRecipient,
    includeimages: configuration.includeFiles,
  }),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    const placeholderMenuOptions = createMemo(() =>
      createDynamicValueOptions(t, {
        fields: fieldService
          .getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS)
          .flatMap((field) => toFieldValueOptions(field)),
        subforms: [],
        customVariables: variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES).map(toVariableValueOption),
        metaVariables: ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
      }),
    );

    return (
      <>
        <SingleSelectInput
          label={t(props.propertyKeys.protocol)}
          value={props.integration.configuration.protocol}
          onSelect={(value) => props.updateConfiguration("protocol", value)}
          options={[
            { variant: "text", value: "ftps", label: "FTPS" },
            { variant: "text", value: "sftp", label: "SFTP" },
            { variant: "text", value: "ftp", label: "FTP" },
          ]}
          errorMessage={props.getError("protocol")}
          required
          info={{
            title: t(props.propertyKeys.protocol),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.protocol }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_PROTOCOL_DESCRIPTION),
          }}
          sameWidth
        />
        <Checkbox
          block
          label={t(props.propertyKeys.disableSslVerification)}
          checked={!props.integration.configuration.disableSslVerification}
          setChecked={(value) => {
            props.updateConfiguration("disableSslVerification", value === false);
          }}
          info={{
            title: t(props.propertyKeys.disableSslVerification),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.disableSslVerification,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_DISABLE_SSL_VERIFICATION_DESCRIPTION),
          }}
        />
        <Checkbox
          block
          label={t(props.propertyKeys.implicitFtps)}
          checked={props.integration.configuration.implicitFtps}
          setChecked={(value) => {
            props.updateConfiguration("implicitFtps", value);
          }}
          info={{
            title: t(props.propertyKeys.implicitFtps),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.implicitFtps,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_IMPLICIT_FTPS_DESCRIPTION),
          }}
        />
        <TextInput
          label={t(props.propertyKeys.server)}
          value={props.integration.configuration.server}
          onBlur={(e) => {
            props.updateConfiguration("server", e.target.value);
          }}
          errorMessage={props.getError("server")}
          required
          info={{
            title: t(props.propertyKeys.server),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.server }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_SERVER_DESCRIPTION),
          }}
        />
        <NumberInput
          label={t(props.propertyKeys.port)}
          value={props.integration.configuration.port}
          minValue={0}
          maxValue={PORT_MAX_VALUE}
          onBlur={(value) => props.updateConfiguration("port", value)}
          errorMessage={props.getError("port")}
          required
          info={{
            title: t(props.propertyKeys.port),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.port }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_PORT_DESCRIPTION),
          }}
        />
        <MixedInput
          label={t(props.propertyKeys.path)}
          value={props.integration.configuration.path}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("path", value);
          }}
          errorMessage={props.getError("path")}
          required
          info={{
            title: t(props.propertyKeys.path),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.path }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_PATH_DESCRIPTION),
          }}
        />
        <Checkbox
          block
          label={t(props.propertyKeys.autoAppendPath)}
          checked={props.integration.configuration.autoAppendPath}
          setChecked={(value) => {
            props.updateConfiguration("autoAppendPath", value);
          }}
          info={{
            title: t(props.propertyKeys.autoAppendPath),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.autoAppendPath,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_AUTO_APPEND_PATH_DESCRIPTION),
          }}
        />
        <MixedInput
          label={t(props.propertyKeys.jsonFilename)}
          value={props.integration.configuration.jsonFilename}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("jsonFilename", value);
          }}
          errorMessage={props.getError("jsonFilename")}
          info={{
            title: t(props.propertyKeys.jsonFilename),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.jsonFilename,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_JSON_FILENAME_DESCRIPTION),
          }}
        />
        <TextInput
          label={t(props.propertyKeys.username)}
          value={props.integration.configuration.username}
          onBlur={(e) => {
            props.updateConfiguration("username", e.target.value);
          }}
          errorMessage={props.getError("username")}
          required
          info={{
            title: t(props.propertyKeys.username),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.username }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_USERNAME_DESCRIPTION),
          }}
        />
        <TextInput
          label={t(props.propertyKeys.password)}
          value={props.integration.configuration.password}
          onBlur={(e) => {
            props.updateConfiguration("password", e.target.value);
          }}
          errorMessage={props.getError("password")}
          required
          info={{
            title: t(props.propertyKeys.password),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.password }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_PASSWORD_DESCRIPTION),
          }}
        />
        <Checkbox
          block
          label={t(props.propertyKeys.includePdf)}
          checked={props.integration.configuration.includePdf}
          setChecked={(value) => {
            props.updateConfiguration("includePdf", value);
          }}
          info={{
            title: t(props.propertyKeys.includePdf),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.includePdf }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_INCLUDE_PDF_DESCRIPTION),
          }}
        />
        <Checkbox
          block
          label={t(props.propertyKeys.storePdfWithoutRecipient)}
          checked={props.integration.configuration.storePdfWithoutRecipient}
          setChecked={(value) => {
            props.updateConfiguration("storePdfWithoutRecipient", value);
          }}
          info={{
            title: t(props.propertyKeys.storePdfWithoutRecipient),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.storePdfWithoutRecipient,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_STORE_PDF_WITHOUT_RECIPIENT_DESCRIPTION),
          }}
        />
        <Checkbox
          block
          label={t(props.propertyKeys.includeFiles)}
          checked={props.integration.configuration.includeFiles}
          setChecked={(value) => {
            props.updateConfiguration("includeFiles", value);
          }}
          info={{
            title: t(props.propertyKeys.includeFiles),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.includeFiles,
            }),
            description: t(($) => $.FTPS_INTEGRATION_CONFIGURATION_INCLUDE_FILES_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(FTPSIntegration);
